import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useMatch } from 'react-router-dom'

// import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, routes } from '../../constants/routes'

import { createSelector } from '@reduxjs/toolkit'
import { clearMusicSource, setMusicSource } from '../../reducers/sound'
import { Helmet } from 'react-helmet'
import StorySection from './story-section'
import StoryRealWorld from './story-real-world'
import StoryIntroduction from './story-introduction'
import NavActions from '../elements/nav-actions'

import { ReactComponent as TopTear } from '../../assets/svgs/top-tear-collage-02.svg'
import StoryCollageMobile from './story-collage-mobile'

const selectLocationStories = state => state.locationStories
const selectLocationSlug = (state, location) => location
const selectKeyIssueCategories = state => state.keyIssueCategories
const selectKeyIssues = state => state.keyIssues
const selectRelatedRealWorlds = state => state.realWorldStories
const selectStory = (state, storyObj) => storyObj

const withBaseUrl = (WrappedComponent) => {
    return function (props) {
        let match = useMatch(routes.clayvilleView.path)
        return <WrappedComponent params={match?.params} {...props} />
    }
}

const makeStory = createSelector(
    [selectLocationSlug, selectLocationStories],
    (locationSlug, stories) => {
        return stories.find(story => story.locationSlug === locationSlug)
    }
)

const makeLocationKeyIssues = createSelector(
    [selectKeyIssueCategories, selectStory],
    (keyIssueCategories, story) => {
        if (!story) {
            return null
        }
        return keyIssueCategories.find(ki => {
            return story.locationKeyIssue === ki.uuid
        })
    }
)

const makeRelatedIssues = createSelector(
    [selectKeyIssues, selectStory],
    (keyIssues, story) => {
        if (story?.relatedKeyIssueIds) {
            return keyIssues.filter(ki =>
                story.relatedKeyIssueIds.includes(ki.uuid)
            )
        }
        return []
    }
)

const makeRelatedRealWorlds = createSelector(
    [selectRelatedRealWorlds, selectStory],
    (realWorlds, story) => {
        if (story?.relatedRealStoryIds) {
            return realWorlds.filter(rw =>
                story.relatedRealStoryIds.includes(rw.uuid)
            )
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    let story = makeStory(state, ownProps?.params?.location)
    return {
        navState: state.nav,
        locationKeyIssue: makeLocationKeyIssues(state, story),
        story: story,
        uiHeight: state.ui.height,
        uiDevice: state.ui.deviceType,
        relatedIssues: makeRelatedIssues(state, story),
        relatedRealWorlds: makeRelatedRealWorlds(state, story)
    }
}

const ClayvilleLocationMobile = ({ dispatch, navState, params, story, uiHeight, uiDevice, locationKeyIssue, relatedIssues, relatedRealWorlds }) => {
    const [scrolledHalf, setScrolledHalf] = useState(false)
    const [scrolledSeventyFive, setScrolledSeventyFive] = useState(false)
    const [scrolledNinety, setScrolledNinety] = useState(false)
    const [activeSectionNum, setActiveSectionNum] = useState(1)
    const componentName = 'Clayville Location'
    const slideHeight = uiDevice === 'mobile' ? uiHeight * 1.5 : uiHeight * 5
    const [totalScroll, setTotalScroll] = useState(0)
    const color = locationKeyIssue?.color || 'blue'

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            // console.log('PRELOADING: story')
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            // console.log('ANIMATING_IN: story')
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    const handlePreloadingState = async () => {
        dispatch(fetchApiData(apiResourceEndpoint(`locations/${params.location}`, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        if (navState.theme !== 'white' && ['blue', 'magenta', 'teal'].includes(color)) {
            dispatch(saveNavTheme('white'))
        } else {
            // Otherwise make sure it is grey
            dispatch(saveNavTheme('grey'))
        }
        if (story.locationPrimaryAudio) {
            dispatch(setMusicSource(story.locationPrimaryAudio))
        } else {
            dispatch(clearMusicSource())
        }
    }

    const handleAnimatingOutState = () => {
        setTotalScroll(0)
        dispatch(completeAnimatingOut())
    }

    useEffect(() => {
        if (!story) {
            return
        }
        let possibleScroll = Math.round((story.sections.length + 0.5) * slideHeight - (slideHeight / 2))
        let scrollPercentage = Math.round((totalScroll / possibleScroll) * 100)
        if (scrollPercentage > 100) {
            scrollPercentage = 100
        }
        if (scrollPercentage < 0) {
            scrollPercentage = 0
        }

        if (scrollPercentage >= 50 && !scrolledHalf) {
            setScrolledHalf(true)
            dispatchGtmScrollEvent(50)
        }
        if (scrollPercentage >= 75 && !scrolledSeventyFive) {
            setScrolledSeventyFive(true)
            dispatchGtmScrollEvent(75)
        }
        if (scrollPercentage >= 90 && !scrolledNinety) {
            setScrolledNinety(true)
            dispatchGtmScrollEvent(90)
        }
    }, [totalScroll, scrolledHalf, scrolledSeventyFive, scrolledNinety])

    const dispatchGtmScrollEvent = (value) => {
        let dataLayer = window.dataLayer || []
        dataLayer.push({
            event: 'scrollThreshold',
            scrollPercent: value
        })
    }

    const handleNavClick = (index) => {
        let newIdx = index
        if (newIdx !== activeSectionNum && newIdx >= 1 && newIdx <= story.sections.length + 3) {
            setActiveSectionNum(newIdx)
            let target = document.getElementById(`section-${newIdx}`)
            if (target) {
                target.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING' || !story) {
        return null
    }

    const renderLocationSections = () => {
        let allSections = []
        story.sections.forEach((section, index) => {
            allSections.push(
                <StorySection
                    key={index + 2}
                    index={index + 2}
                    section={section}
                    color={color}
                    isMobile={true}
                    onSetActiveSectionNum={setActiveSectionNum}
                />
            )
        })
        return allSections
    }

    return (
        <>
            <Helmet>
                <title>{story.pageTitle}</title>
                <meta name="description" content={story.metaDescription} />
                <meta property="og:title" content={story.pageTitle} />
                <meta property="og:description" content={story.metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={story.metaImage?.url} />
            </Helmet>
            <div className={`clayville-story ${color}-bg`}>
                <div className="location-wrapper">
                    <StoryIntroduction
                        index={1}
                        color={color}
                        story={story}
                        isMobile={true}
                        onSetActiveSectionNum={setActiveSectionNum} />
                    {renderLocationSections()}
                    <div className="real-world-tears top-tear-collage">
                        <TopTear />
                    </div>
                </div>
                <div className="location-wrap-up">
                    <StoryRealWorld
                        index={story.sections.length + 2}
                        relatedRealWorlds={relatedRealWorlds}
                        color={color}
                        isMobile={true}
                        locationName={story.locationName}
                        onSetActiveSectionNum={setActiveSectionNum}
                    />
                    <StoryCollageMobile
                        index={story.sections.length + 3}
                        storyCollageHeader={story.locationCollageHeader}
                        storyCollageItems={story.locationFooterCollage}
                        color={color}
                        isMobile={true}
                        locationName={story.locationName}
                        onSetActiveSectionNum={setActiveSectionNum}
                    />
                </div>
            </div>
            <NavActions
                activeChapter={activeSectionNum}
                chapterLength={story.sections.length + 2}
                setActiveChapterIndex={handleNavClick}
            />
        </>
    )
}

export default withBaseUrl(connect(mapStateToProps)(ClayvilleLocationMobile))
